import type { ClipboardPasteSegment } from './types';

/** Unique data-URI image sources from HTML clipboard content. */
export function extractDataUriImagesFromHtml(html: string): string[] {
  if (!html || !html.includes('data:image')) return [];

  const template = document.createElement('template');
  template.innerHTML = html;
  const uris = new Set<string>();

  template.content.querySelectorAll('img[src^="data:image"]').forEach((img) => {
    const src = img.getAttribute('src');
    if (src?.startsWith('data:image')) {
      uris.add(src);
    }
  });

  return Array.from(uris);
}

/** Remove data-URI `<img>` tags from HTML, leaving other content. */
export function stripDataUriImagesFromHtml(html: string): string {
  if (!html || !html.includes('data:image')) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('img[src^="data:image"]').forEach((img) => img.remove());

  return template.innerHTML.trim();
}

/** Ordered clipboard segments preserving text/image interleaving from HTML (nested images included). */
export function extractOrderedSegmentsFromHtml(html: string): ClipboardPasteSegment[] {
  if (!html || !html.includes('data:image')) return [];

  const template = document.createElement('template');
  template.innerHTML = html;
  const segments: ClipboardPasteSegment[] = [];

  const flushHtmlNodes = (nodes: ChildNode[]) => {
    if (!nodes.length) return;
    const wrapper = document.createElement('div');
    nodes.forEach((node) => wrapper.appendChild(node.cloneNode(true)));
    const piece = wrapper.innerHTML.trim();
    if (piece) segments.push({ kind: 'html', html: piece });
  };

  const hasNestedDataImages = (el: Element) => !!el.querySelector('img[src^="data:image"]');

  const walk = (parent: ParentNode) => {
    const pending: ChildNode[] = [];

    Array.from(parent.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        if (el.tagName === 'IMG') {
          const src = el.getAttribute('src');
          if (src?.startsWith('data:image')) {
            flushHtmlNodes(pending);
            pending.length = 0;
            segments.push({ kind: 'image', dataUri: src });

            return;
          }
        }

        if (hasNestedDataImages(el)) {
          flushHtmlNodes(pending);
          pending.length = 0;
          walk(el);

          return;
        }
      }

      pending.push(child);
    });

    flushHtmlNodes(pending);
  };

  walk(template.content);
  template.remove();

  return segments.some((segment) => segment.kind === 'image') ? segments : [];
}
